import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { resources, Resource } from '../mocks/resources';

export interface CaseStudyMetric {
  value: string;
  label: string;
}

export interface CaseStudy {
  id: string;
  slug: string;
  title: string;
  type: 'Case Study';
  topic: string;
  description: string;
  excerpt: string;
  image: string;
  date: string;
  readTime: string;
  tags: string[];
  metrics: CaseStudyMetric[];
  featured: boolean;
  downloadable: boolean;
  status: 'published' | 'draft' | 'archived';
  createdAt?: string;
  updatedAt?: string;
}

interface CaseStudiesContextValue {
  caseStudies: CaseStudy[];
  loading: boolean;
  error: string | null;
  addCaseStudy: (cs: CaseStudy) => Promise<void>;
  updateCaseStudy: (id: string, updates: Partial<CaseStudy>) => Promise<void>;
  archiveCaseStudy: (id: string) => Promise<void>;
  refreshCaseStudies: () => Promise<void>;
}

const CaseStudiesContext = createContext<CaseStudiesContextValue | null>(null);

const SEED_FLAG = 'cendien_case_studies_seeded_v1';

function mockToCaseStudy(r: Resource): CaseStudy {
  return {
    id: `case-study-${r.id}`,
    slug: r.slug,
    title: r.title,
    type: 'Case Study',
    topic: r.topic,
    description: r.description,
    excerpt: r.excerpt,
    image: r.image,
    date: r.date,
    readTime: r.readTime,
    tags: r.tags,
    metrics: r.metrics || [],
    featured: r.featured,
    downloadable: r.downloadable,
    status: 'published',
  };
}

function toDBRow(cs: Partial<CaseStudy> & { id: string }) {
  const row: Record<string, unknown> = {
    id: cs.id,
    updated_at: new Date().toISOString(),
  };
  if (cs.slug !== undefined) row.slug = cs.slug;
  if (cs.title !== undefined) row.title = cs.title;
  if (cs.topic !== undefined) row.topic = cs.topic;
  if (cs.description !== undefined) row.description = cs.description;
  if (cs.excerpt !== undefined) row.excerpt = cs.excerpt;
  if (cs.image !== undefined) row.image = cs.image;
  if (cs.date !== undefined) row.date = cs.date;
  if (cs.readTime !== undefined) row.read_time = cs.readTime;
  if (cs.tags !== undefined) row.tags = cs.tags;
  if (cs.metrics !== undefined) row.metrics = cs.metrics;
  if (cs.featured !== undefined) row.featured = cs.featured;
  if (cs.downloadable !== undefined) row.downloadable = cs.downloadable;
  if (cs.status !== undefined) row.status = cs.status;
  return row;
}

function fromDBRow(row: Record<string, unknown>): CaseStudy {
  return {
    id: (row.id as string) || '',
    slug: (row.slug as string) || '',
    title: (row.title as string) || '',
    type: 'Case Study',
    topic: (row.topic as string) || '',
    description: (row.description as string) || '',
    excerpt: (row.excerpt as string) || '',
    image: (row.image as string) || '',
    date: (row.date as string) || '',
    readTime: (row.read_time as string) || '',
    tags: (row.tags as string[]) || [],
    metrics: (row.metrics as CaseStudyMetric[]) || [],
    featured: Boolean(row.featured),
    downloadable: row.downloadable !== false,
    status: ((row.status as string) || 'published') as 'published' | 'draft' | 'archived',
    createdAt: (row.created_at as string) || undefined,
    updatedAt: (row.updated_at as string) || undefined,
  };
}

export function CaseStudiesProvider({ children }: { children: ReactNode }) {
  const [caseStudies, setCaseStudies] = useState<CaseStudy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCaseStudies = useCallback(async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('case_studies')
        .select('*')
        .order('created_at', { ascending: true });

      if (fetchError) throw fetchError;

      let rows = data || [];

      // One-time seed from mock data so existing case studies appear in the database
      if (rows.length === 0 && !localStorage.getItem(SEED_FLAG)) {
        const seeds = resources.filter((r) => r.type === 'Case Study').map(mockToCaseStudy);
        if (seeds.length > 0) {
          const base = Date.now();
          const seedRows = seeds.map((cs, i) => ({
            ...toDBRow(cs),
            created_at: new Date(base + i * 1000).toISOString(),
          }));
          const { error: seedError } = await supabase.from('case_studies').insert(seedRows);
          if (!seedError) {
            localStorage.setItem(SEED_FLAG, '1');
            const { data: seeded } = await supabase
              .from('case_studies')
              .select('*')
              .order('created_at', { ascending: true });
            rows = seeded || [];
          }
        }
      }

      setCaseStudies(rows.map(fromDBRow));
      setError(null);
    } catch (err) {
      setError('Failed to load case studies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCaseStudies();
  }, [fetchCaseStudies]);

  const addCaseStudy = useCallback(async (cs: CaseStudy) => {
    setCaseStudies((prev) => [...prev, cs]);
    try {
      const row = { ...toDBRow(cs), created_at: new Date().toISOString() };
      const { error: insertError } = await supabase.from('case_studies').insert([row]);
      if (insertError) throw insertError;
    } catch (err) {
      setCaseStudies((prev) => prev.filter((c) => c.id !== cs.id));
      throw err;
    }
  }, []);

  const updateCaseStudy = useCallback(async (id: string, updates: Partial<CaseStudy>) => {
    setCaseStudies((prev) => prev.map((c) => (c.id === id ? { ...c, ...updates } : c)));
    try {
      const row = toDBRow({ id, ...updates });
      const { error: updateError } = await supabase.from('case_studies').update(row).eq('id', id);
      if (updateError) throw updateError;
    } catch (err) {
      fetchCaseStudies();
      throw err;
    }
  }, [fetchCaseStudies]);

  // Never-delete: archiving only flips status to 'archived' — the row is kept.
  const archiveCaseStudy = useCallback(async (id: string) => {
    const prev = caseStudies.find((c) => c.id === id);
    setCaseStudies((prevList) => prevList.map((c) => (c.id === id ? { ...c, status: 'archived' } : c)));
    try {
      const { error: archiveError } = await supabase
        .from('case_studies')
        .update({ status: 'archived', updated_at: new Date().toISOString() })
        .eq('id', id);
      if (archiveError) throw archiveError;
    } catch (err) {
      if (prev) setCaseStudies((prevList) => prevList.map((c) => (c.id === id ? prev : c)));
      throw err;
    }
  }, [caseStudies]);

  return (
    <CaseStudiesContext.Provider
      value={{
        caseStudies,
        loading,
        error,
        addCaseStudy,
        updateCaseStudy,
        archiveCaseStudy,
        refreshCaseStudies: fetchCaseStudies,
      }}
    >
      {children}
    </CaseStudiesContext.Provider>
  );
}

export function useCaseStudies() {
  const ctx = useContext(CaseStudiesContext);
  if (!ctx) throw new Error('useCaseStudies must be used within CaseStudiesProvider');
  return ctx;
}